<template>
  <div
    class="c-lookup"
    ref="lookup"
    :class="{
      'c-lookup--fixed-height': isScroll,
      'c-lookup--mobile': responsive !== 'desktop',
      'c-lookup--has-carousel':
        this.resultsList.length > 1 && responsive !== 'desktop',
    }"
  >
    <div
      class="c-lookup__cross-button"
      v-shortkey="['esc']"
      @shortkey="$emit('close-lookup')"
      @keydown.enter="$emit('close-lookup')"
      @click="$emit('close-lookup')"
      tabindex="0"
    >
      <span class="c-lookup__cross-button-cross"> </span>
      <span class="c-lookup__cross-button-cross"> </span>
    </div>
    <h3 class="c-lookup__title">{{ $t('LOOKUP_TITLE') }}</h3>
    <div class="c-lookup__col">
      <span>{{ $t('FROM') }}</span>
      <language-select
        ref="select"
        :languages="langFrom"
        :selected="bLang"
        @lang-changed="changeBookLanguage"
      />
    </div>
    <div class="c-lookup__arrow">
      <arrow-right />
    </div>
    <div class="c-lookup__col">
      <span>{{ $t('TO') }}</span>
      <language-select
        :languages="langTo"
        :selected="userLang"
        @lang-changed="changeUserLanguage"
      />
    </div>
    <hr class="c-lookup__line" />
    <simplebar
      class="scrollbox"
      data-simplebar-auto-hide="false"
      data-simplebar-force-visible="true"
    >
      <div
        v-if="resultsList.length && hasAnyTranslation"
        class="c-lookup__content-wrapper"
      >
        <div
          v-if="responsive !== 'desktop' && resultsList.length > 1"
          class="c-lookup__content"
        >
          <the-carousel
            ref="flickity"
            :options="flickityOptions"
            :manualInit="true"
            class="carousel"
            @carousel-index="updateCarouselIndex"
          >
            <li
              class="c-lookup__item carousel-cell"
              v-bind:key="result.id"
              v-for="result in resultsList"
            >
              <lookup-box
                class="c-lookup__lookup-box"
                ref="lb"
                :style="{ height: maxHeight ? maxHeight + 'px' : 'auto' }"
                :result="result"
                :term="term"
                :language="bookLang.code"
                :responsive="responsive"
                @toggle-show-details="updateCurrentSlideShowDetails"
                @toggle-clicked="setHeight()"
              />
            </li>
          </the-carousel>
        </div>
        <div v-else class="c-lookup__content">
          <ul
            v-if="resultsLoaded && resultsList.length"
            ref="content"
            class="c-lookup__list"
          >
            <li
              class="c-lookup__item"
              v-bind:key="result.id"
              v-for="result in resultsList"
            >
              <lookup-box
                class="c-lookup__lookup-box"
                ref="lb"
                :result="result"
                :term="term"
                :language="bookLang.code"
                :responsive="responsive"
                @toggle-show-details="updateCurrentSlideShowDetails"
                @toggle-clicked="setHeight()"
              />
            </li>
          </ul>
        </div>
      </div>
      <lookup-empty v-else :term="term" :lookupLoaded="resultsLoaded" />
    </simplebar>
    <div
      class="c-lookup__bottombar"
      v-if="responsive !== 'desktop' && resultsList.length > 1"
    >
      <div class="c-lookup__slider-controls">
        <button
          v-show="carouselIndex !== 0"
          class="c-lookup__slider-controller"
          :aria-label="$t('SLIDER_PREVIOUS')"
          @click="$refs.flickity.$flickity.previous()"
        >
          &lt; {{ $t('SLIDER_PREVIOUS') }}
        </button>
        <button
          v-show="resultsList.length - 1 !== carouselIndex"
          class="c-lookup__slider-controller c-lookup__slider-controller--next"
          :aria-label="$t('SLIDER_NEXT')"
          @click="$refs.flickity.$flickity.next()"
        >
          {{ $t('SLIDER_NEXT') }} &gt;
        </button>
      </div>
      <button
        v-if="showMoreMobileBtn"
        class="c-lookup__show-hide-details-btn"
        :aria-expanded="`${currentSlideShowDetails}`"
        @click="toggleDetailsLB()"
      >
        <span class="c-lookup__arrow-icon-wrapper">
          <arrow-icon
            class="c-lookup__arrow-icon"
            :class="{ 'c-lookup__arrow-icon--rotate': currentSlideShowDetails }"
            fill="#fff"
          />
        </span>
        <span class="c-lookup__show-hide-details-btn__text">
          {{
            currentSlideShowDetails ? $t('HIDE_DETAILS') : $t('VIEW_DETAILS')
          }}
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { UserDB } from '@/services/db'
import Simplebar from 'simplebar-vue'

import multiDictService from '@/services/multiDict'

import LookupLanguageSelect from './LookupLanguageSelect'
import LookupBox from '@/components/LookupBox'
import TheCarousel from '@/components/TheCarousel'
import LookupEmpty from '@/components/LookupEmpty'

import ArrowIcon from '@/assets/svg/Arrow'
import ArrowRight from '@/assets/svg/ArrowRight'

let db = new UserDB()

export default {
  name: 'Lookup',

  components: {
    'language-select': LookupLanguageSelect,
    'lookup-box': LookupBox,
    simplebar: Simplebar,
    'arrow-icon': ArrowIcon,
    'arrow-right': ArrowRight,
    'the-carousel': TheCarousel,
    'lookup-empty': LookupEmpty,
  },

  props: {
    term: String,
    bookSource: String,
    bookLang: {},
  },

  data() {
    return {
      bLang: {},
      userLang: {},
      langFrom: [],
      langTo: [],
      resultsList: Array,
      language: String,
      isScroll: false,
      flickityOptions: {
        draggable: true,
        groupCells: false,
        cellAlign: 'left',
        prevNextButtons: false,
        pageDots: false,
        wrapAround: false,
        contain: true,
        resize: true,
      },
      currentSlideShowDetails: false,
      carouselIndex: 0,
      hasAnyTranslation: false,
      maxHeight: null,
      resultsLoaded: false,
    }
  },

  computed: {
    showMoreMobileBtn() {
      return this.responsive === 'mobile' && this.resultsList.length > 0
    },

    ...mapState('userSettings', {
      responsive: state => state.responsive,
      dictLang: state => state.dictLang,
      dictName: state => state.dictName,
    }),
  },

  updated() {
    this.setHeight()
    this.setResultsHeight()
  },

  destroyed() {
    window.removeEventListener('resize', this.setHeight)
  },

  methods: {
    async getLanguages() {
      let listFrom = [],
        listTo = [],
        languages = await multiDictService.getLanguages()
      languages.languages.forEach(function(language) {
        if (language.props.base) {
          listFrom.push(language)
        } else {
          listTo.push(language)
        }
      })
      this.langFrom = listFrom
      this.langTo = listTo
    },

    toggleDetailsLB() {
      this.$refs.lb.forEach(lb => {
        lb.toggleDetails()
      })
      if (this.resultsList.length > 1 && this.responsive !== 'desktop') {
        this.maxHeight = null

        this.$nextTick(() => {
          if (!this.$refs.flickity.$flickity) return
          this.$refs.flickity.$flickity.resize()
          this.setResultsHeight()
        })
      }
    },

    updateCarouselIndex(i) {
      this.carouselIndex = i
      this.currentSlideShowDetails = this.$refs.lb[i].showDetails
    },

    updateCurrentSlideShowDetails(val) {
      this.currentSlideShowDetails = val
    },

    async newLookup(e) {
      if (typeof e === 'undefined') return
      // removes unwanted chars
      const phrase = e.trim().replace(/^[,.?(«]+|[,.?)»]+$/g, '')

      try {
        this.resultsList = await multiDictService.getPhrases(
          phrase,
          this.bLang.code,
          this.userLang.code,
          this.bookSource,
        )
        this.resultsList = [...this.resultsList].filter(
          result => result.translation,
        )
        this.hasAnyTranslation = this.resultsList.find(
          result => result.translation,
        )
      } catch (err) {
        return
      }

      this.resultsLoaded = true
      this.maxHeight = null

      if (this.$refs.flickity) {
        this.$refs.flickity.$flickity.destroy()
      }

      if (this.responsive !== 'desktop' && this.resultsList.length > 1) {
        setTimeout(() => {
          this.$refs.flickity.init()
        }, 0)
      }

      // Wait for DOM to be updated and then set height
      this.$nextTick().then(() => {
        this.setHeight()
        this.setResultsHeight()
        this.carouselIndex = 0
      })
    },

    changeBookLanguage(lang) {
      this.bLang = lang
      this.newLookup(this.term)
    },

    changeUserLanguage(lang) {
      this.userLang = lang
      //save to db
      this.setDictLang({ code: this.userLang.code, name: this.userLang.name })
      this.newLookup(this.term)
    },

    setHeight() {
      setTimeout(() => {
        let content
        if (this.resultsList.length > 1 && this.responsive !== 'desktop') {
          if (!this.$refs.flickity.$el) return
          content = this.$refs.flickity.$el.getBoundingClientRect()
        } else {
          // check if dom available or not
          content = this.$refs.content
            ? this.$refs.content.getBoundingClientRect()
            : undefined
        }
        const lookup = this.$refs.lookup.getBoundingClientRect()

        if (content !== undefined) {
          this.isScroll = content.height > lookup.height - 132
        } else {
          this.isScroll = false
        }
      }, 0)

      if (this.resultsList.length > 1 && this.responsive !== 'desktop') {
        this.$nextTick(() => {
          if (!this.$refs.flickity.$flickity) return
          this.$refs.flickity.$flickity.resize()
        })
      }
    },

    setResultsHeight() {
      if (
        this.responsive !== 'desktop' &&
        this.resultsList.length > 1 &&
        this.$refs.lb
      ) {
        let heights = []

        this.$refs.lb.forEach(lb => {
          heights.push(lb.$el.clientHeight ? lb.$el.clientHeight : 0)
        })
        this.maxHeight = Math.max(...heights)
      }
    },

    ...mapActions({
      setDictLang: 'userSettings/setDictLang',
    }),
  },

  watch: {
    term(value) {
      this.newLookup(value)
    },

    responsive(type) {
      if (type !== 'desktop' && this.resultsList.length > 1) {
        setTimeout(() => {
          this.$refs.flickity.init()
        }, 0)
      }
    },
  },

  async mounted() {
    //can't update prop directly
    this.bLang = this.bookLang

    const translate = (await db.getDictLang('lang')) || {
      code: this.dictLang,
      name: this.dictName,
    }
    this.userLang = translate
    this.setDictLang(this.userLang)

    this.getLanguages()
    this.newLookup(this.term)
    window.getSelection().removeAllRanges()

    this.setHeight()
    window.addEventListener('resize', this.setHeight)
  },
}
</script>

<style lang="scss">
@import '../../node_modules/simplebar/dist/simplebar.min.css';
@import '@/assets/scss/_vars.scss';
@import '@/assets/scss/_mixin.scss';

$topbarheight: 132px;
$bottombarheight: 94px;

.c-lookup {
  position: fixed;
  top: 10vh;
  left: calc(50% - 150px);
  z-index: 9999;

  width: 430px;
  height: auto;
  max-height: 80%;

  margin: 0;
  padding: 1em 1.2em;
  border-radius: 0.75em;
  background: $blue;

  color: $white;

  &--mobile {
    top: auto;
    left: 0;
    right: 0;
    bottom: 0;
    width: auto;
    max-height: 100vh;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;

    z-index: 9999;
  }

  &--has-carousel {
    padding-bottom: $bottombarheight;
  }

  &.c-lookup--fixed-height {
    height: 80%;
  }
  .scrollbox {
    height: calc(100% - #{$topbarheight});
  }
  .simplebar-track {
    right: -15px;

    pointer-events: auto;
  }
  .simplebar-scrollbar:before {
    background: $white;
  }
}
.c-lookup__content {
  height: calc(100% - 48px);
}

.c-lookup__title {
  text-align: left;
  font-weight: 700;
}

.c-lookup__item {
  &.carousel-cell {
    width: 88vw;
    margin-right: 1rem;
  }

  list-style-type: none;

  &:last-child .c-lookup__lookup-box {
    margin-bottom: 0;
  }
}

.c-lookup__col {
  width: 45%;
  display: inline-block;
  text-align: left;
  font-size: 0.8rem;
}

.c-lookup__arrow {
  width: 10%;
  display: inline-block;
  text-align: center;
  vertical-align: middle;
}
.c-lookup__line {
  background: $light-blue;
  height: 1px;
}
.c-lookup__cross-button {
  position: absolute;
  right: 0.75em;
  top: 0.75em;

  width: 1.5em;
  height: 1.5em;

  &:focus {
    @include a11yOutline();
  }
}
.c-lookup__cross-button-cross {
  position: absolute;
  transform: rotate(45deg);

  height: 25px;

  background-color: $white;
  color: $white;
  border: 2px solid $white;
  margin-top: -3px;
  margin-left: -2px;
  cursor: pointer;
}
.c-lookup__cross-button-cross:last-child {
  transform: rotate(-45deg);
}
.c-lookup__empty {
  padding: 4em 2rem 5rem;
  font-size: 1rem;
}

.c-lookup__term {
  font-size: 2rem;
  font-weight: 500;
  margin-bottom: 0.8rem;
}

.c-lookup__slider-controls {
  display: flex;
  justify-content: space-between;
  // fix simplebar hiding outline (size of outline added)
  margin: 0.75rem 4px 4px;
}

.c-lookup__slider-controller {
  @include btnReset();
  cursor: pointer;
  font-weight: 700;
  text-transform: lowercase;

  @include a11yOutline();

  &--next {
    margin-left: auto;
  }
}

.c-lookup__show-hide-details-btn {
  @include btn();
  display: flex;

  font-weight: 700;
  border: 0;
  background: $orange;
  color: $white;
  text-transform: uppercase;
  margin: 1em auto 0;

  &:hover {
    background: $orange-hover;
  }

  @include a11yOutline();

  margin-bottom: 1rem;
}

.show-hide-details-btn__text {
  line-height: 0;
}

.c-lookup__arrow-icon-wrapper {
  width: 1rem;
  height: 1rem;
  position: relative;
}

.c-lookup__arrow-icon {
  top: -10px;
  left: -10px;
  position: absolute;
  transform: rotate(180deg);
  transform-origin: 50% 58%;

  &--rotate {
    transform: rotate(0deg);
  }
}

.c-lookup__bottombar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;

  background: $blue;
}
</style>
